import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getYearAnalyticsInfo } from '../../../server/server'
import { usePageTitle } from '../../../utils/util'
import { useDaysUrlFilter } from '../useAnalyticsUrlFilters'
import SingleAnalyticsHeader from '../student/SingleAnalyticsHeader'
import DayFilteringWidget from '../student/DayFilteringWidget'
import { daysFilterList } from '../class/ClassAnalyticsWidgets'
import CheckInWidget from '../student/CheckInWidget'
import DetailsOnEmotions from '../student/DetailsOnEmotions'
import ReasonsForEmotions from '../student/ReasonsForEmotionsWidget'
import ParticipationWidget from '../student/ParticipationWidget'
import { CounsellorCardSummary } from '../counsellor/CounsellorCardData'
import { CounsellorClassFilters } from '../counsellor/CounsellorCardAnalytics'
import CounsellorAnalyticsLayout from '../CounsellorAnalyticsLayout'
import AnalyticsWidgetEvents from '../../../analytics/AnalyticsWidgetEvents'
import Loading from '../../Loading'
import YearComparisonWidget from './YearComparisonWidget'
import { yearAnalyticsScope } from './yearAnalyticsFilters'
import StudentsIncludedSummary from '../StudentsIncludedSummary'

export default function YearAnalyticsHome() {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const scope = useMemo(() => yearAnalyticsScope(location.search), [location.search])
    const { daysFilter, setDaysFilter } = useDaysUrlFilter()
    const [info, setInfo] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    usePageTitle('Upstrive Education - Year Analytics')

    useEffect(() => {
        let active = true
        setInfo(null)
        setLoading(true)
        setError('')
        getYearAnalyticsInfo({ yearGroup: scope.yearGroup }).then((response) => {
            if (!active) return
            if (response.status === 200) setInfo(response.data)
            else setError(response.status === 403 ? 'You do not have access to this year' : 'Unable to load year analytics')
            setLoading(false)
        })
        return () => { active = false }
    }, [scope.yearGroup])

    const invalidClasses = info && scope.classIds?.some((id) => !info.classes.some((item) => item.id === id))
    const selectClasses = (ids) => {
        const params = new URLSearchParams(history.location.search)
        params.delete('classId')
        params.delete('selection')
        if (ids !== null) {
            params.set('selection', 'custom')
            if (ids.length) params.set('classId', ids.join(','))
        }
        history.replace({ ...history.location, search: `?${params}` })
    }
    const classes = info?.classes.map((item) => ({
        code: item.id,
        title: item.name,
        studentCount: item.studentCount,
        checked: scope.classIds === null || scope.classIds.includes(item.id),
    }))
    const updateClasses = (items) => {
        selectClasses(items.every((item) => item.checked) ? null : items.filter((item) => item.checked).map((item) => item.code))
    }
    const scopeLabel = info && `${t('Year')} ${info.year}${scope.classIds === null ? '' : ` · ${info.classes.filter((item) => scope.classIds.includes(item.id)).map((item) => item.name).join(', ')}`}`

    const available = info?.year === scope.yearGroup && !invalidClasses

    return (
        <CounsellorAnalyticsLayout
            key={scope.yearGroup}
            header={<SingleAnalyticsHeader showSearch={!!available} yearGroup={scope.yearGroup} />}
            title={t('Year {{year}} analytics', { year: scope.yearGroup })}
            daysFilter={daysFilter}
            setDaysFilter={setDaysFilter}
            card={available && (
                <div className="min-w-[408px] max-w-[408px] h-full bg-white rounded-[24px] ">
                    <CounsellorCardSummary data={info} title={`${t('Year')} ${info.year}`} breakdownLabel={t('Year emotional breakdown')} />
                    <div className="mt-[10px] pb-8 px-8">
                        <div className="relative">
                            <CounsellorClassFilters data={classes} setData={updateClasses} infoText="Select which classes to include in the year analytics view." />
                        </div>
                    </div>
                </div>
            )}
        >
            <div className="flex-1 overflow-x-hidden px-2 pt-[20px]  overflow-y-auto">
                {loading && <div className="p-8"><Loading /></div>}
                {(error || invalidClasses) && <p role="alert" className="p-8 text-darkenPurple">{t(error || 'Those classes are outside the selected year')}</p>}
                {available && (
                    <>
                        <DayFilteringWidget daysFilter={daysFilter} daysFilterList={daysFilterList} setDaysFilter={setDaysFilter} />
                        <div className="w-full h-[1px] bg-white mt-4"></div>
                        {scope.classIds?.length === 0 ? <>
                            <StudentsIncludedSummary data={{ studentsIncluded: 0, studentsCheckedIn: 0 }} daysFilter={daysFilter} scope={scope} />
                            <p role="status" className="bg-white rounded-[24px] p-6 mt-4 text-darkenPurple">{t('Select at least one class to view analytics')}</p>
                        </> : (
                            <>
                                <AnalyticsWidgetEvents subjectId={`year:${scope.yearGroup}`} widgets={[
                                    { key: 'check-in', label: 'Check-in' }, { key: 'wellbeing-comparison', label: 'Wellbeing comparison' },
                                    { key: 'emotion-details', label: 'Emotion details' }, { key: 'emotion-reasons', label: 'Reasons for emotions' },
                                    { key: 'alerts', label: 'Alerts' }, { key: 'participation', label: 'Participation' },
                                ]} />
                                <CheckInWidget daysFilter={daysFilter} isCounsellor selectedUsersScope={scope} analyticsType="year" scopeLabel={scopeLabel} />
                                <YearComparisonWidget daysFilter={daysFilter} scope={scope} />
                                <DetailsOnEmotions daysFilter={daysFilter} isCounsellor selectedUsersScope={scope} analyticsType="year" />
                                <ReasonsForEmotions daysFilter={daysFilter} isCounsellor selectedUsersScope={scope} analyticsType="year" />
                                <YearComparisonWidget daysFilter={daysFilter} scope={scope} alerts />
                                <ParticipationWidget daysFilter={daysFilter} isCounsellor selectedUsersScope={scope} />
                            </>
                        )}
                    </>
                )}
            </div>
        </CounsellorAnalyticsLayout>
    )
}
